import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookingModel } from 'ts/flight/interfaces/FlightBookResponse';
import BookSummary from './Summary';
import { getBounds } from 'utils/flight/flight';
import SuccessPassengerDetails from './SuccessPassengerDetails';
import { delay, getBase64Screenshot } from 'utils/utils';
import { submitScreenshots } from 'services/flight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { useGetPage } from 'hooks/useGetPage';
import useFlightDataLayer from 'hooks/useFlightDataLayer';
import { b2b, needToScreenshot } from 'constants/generic';

const FlightSuccessPage: FC = () => {
  const [t] = useTranslation();
  const successRef = useRef<HTMLDivElement>(null);
  
  const { pushFlightPurchase } = useFlightDataLayer();

  useGetPage('Flights/BookComplete');

  const [order, setOrder] = useState<BookingModel>();

  useEffect(() => {
    if (b2b) {
      if (window.location.pathname.toLowerCase() !== `/flight/success`) {
        return;
      }
    }
    const orderStr = sessionStorage.getItem('flightSuccessOrder');
    if (orderStr) {
      const order = JSON.parse(orderStr);
      setOrder(order);

      (async () => {
        await delay(1000);
        pushFlightPurchase();

        if (needToScreenshot && !order.screenshot) {
          const image = await getBase64Screenshot(successRef.current);

          submitScreenshots({
            screenshots: [],
            orderId: order.orderId,
            lastScreenshot: image,
            transactionId: order.transactionId,
          });

          sessionStorage.setItem(
            'flightSuccessOrder',
            JSON.stringify({ ...order, screenshot: true })
          );
        }
      })();
    }
  }, []); // eslint-disable-line

  return (
    <div className="success page-wrapper" ref={successRef}>
      <div className="title">
        {t('flight.order_summary')} #{order?.orderId}
      </div>

      {order?.statusIsOK ? (
        <div className="text-md mb-20">{t('success')}!</div>
      ) : (
        <div className="mb-20">
          <FontAwesomeIcon className="color-danger mr-5" icon={faExclamationTriangle as any} />
          <span className="text-md">{t('failed_order')}</span>
        </div>
      )}
      {order?.orderHasBeenPaid && (
        <div className="text-md mb-20">{t('hotel.paid_successfully')}</div>
      )}
      {!!order?.paxes && (
        <SuccessPassengerDetails
          paxes={order.paxes}
          baggageFlights={order.baggageFlightDetails?.bookFareAndBaggageFlights}
        />
      )}
      {!!order?.flight && (
        <BookSummary
          successPage={true}
          flight={order?.flight}
          bounds={getBounds(order?.flight)}
          couponCode={order?.couponAmount}
          totalPrice={order?.totalPrice}
          totalCurrency={order?.currency}
          setCouponCode={() => {}}
          onClickApply={() => {}}
          addPrice={0}
        />
      )}
    </div>
  );
};

export default withErrorBoundary(FlightSuccessPage, 'FlightSuccessPage');
